import { yupResolver } from '@hookform/resolvers/yup';
import Button from 'monday-ui-react-core/dist/Button';
import Dropdown from 'monday-ui-react-core/dist/Dropdown';
import Toggle from 'monday-ui-react-core/dist/Toggle.js';
import { useContext, useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';
import ConnectStripe from '../../../assets/icons/connect-stripe.svg';
import DisconnectStripe from '../../../assets/icons/disconnect-stripe.svg';
import { useUpdateStripeConfig } from '../../../hooks/useUpdateStripeConfig';
import CustomButton from '../../CustomButton';
import { paymentControllerSchema } from './paymentController.schema';
import { FileContext } from '../../../context/FileContext';
import { useQueryClient } from 'react-query';
import { QUERY_KEYS } from '../../../constants/query-keys';
import useConnectStripe from '../../../hooks/useConnectStripe';
import useDisconnectStripe from '../../../hooks/useDisconnectStripe';
import { useGetEnvelopeQuery } from '../../../hooks/useGetEnvelopeQuery';

const SettingsDialogContainer = styled.form`
  width: 100% !important;
  padding: 1.5rem;

  .title {
    font-size: 18px;
    font-weight: 500;
    margin-bottom: 8px;
  }

  .content {
    font-size: 16px;
    line-height: 1.5;
  }
`;

const StyledDropdown = styled(Dropdown)`
  width: 300px;
  /* z-index: 1000; */
`;

const StripeButton = styled.img`
  padding: 8px 0;
`;

const PaymentConfigurationFields = styled.div`
  display: flex;
  gap: 1rem;
  padding-bottom: 1rem;
  flex-wrap: wrap;

  .text-red-600,
  .text-red-500 {
    color: #d83a52;
  }

  .text-sm {
    font-size: 14px;
  }
`;

const PaymentReminderContainer = styled.div`
  margin: 1rem 0;

  span.inline {
    display: flex;
    align-items: center;
    margin-bottom: 0.8rem;
  }
  .monday-style-toggle_text {
    display: none;
  }
`;

const SaveSettingsContainer = styled.div`
  margin: 1rem 0;
  width: fit-content;
  margin-left: auto;
`;

const DAY_OPTIONS = [
  { value: 1, label: '1 Day' },
  { value: 2, label: '2 Days' },
  { value: 3, label: '3 Days' },
  { value: 4, label: '4 Days' },
  { value: 5, label: '5 Days' },
  { value: 6, label: '6 Days' },
  { value: 7, label: '7 Days' },
];

const PaymentControllerDialog = ({
  t,
  queryData,
  handleSettingsClose,
  setShowAddColumnDialog,
}) => {
  const { triggerToast, envelopeId, triggerSuccessToast } =
    useContext(FileContext);
  const queryClient = useQueryClient();

  const { data: envelopeDetails, isLoading: isLoadingStripeConnectionStatus } =
    useGetEnvelopeQuery(envelopeId, {
      enabled: !!envelopeId,
      refetchOnWindowFocus: false,
      onSuccess: (data) => {},
      initialData: () => {
        const cachedData = queryClient.getQueryData([
          QUERY_KEYS.GET_ENVELOPE_DETAILS,
          envelopeId,
        ]);
        return cachedData;
      },
    });

  const {
    handleSubmit,
    reset,
    control,
    watch,
    formState: { errors },
  } = useForm({
    mode: 'onSubmit',
    defaultValues: {
      isPaymentReminderEnabled: false,
    },
    resolver: yupResolver(paymentControllerSchema),
  });

  useEffect(() => {
    if (envelopeDetails?.stripeConfiguration) {
      reset({
        ...envelopeDetails?.stripeConfiguration,
      });
    }
  }, [envelopeDetails?.stripeConfiguration, reset]);

  const { mutate: updateStripeConfig, isLoading } = useUpdateStripeConfig({
    envelopeId,
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries([
          QUERY_KEYS.GET_ENVELOPE_DETAILS,
          envelopeId,
        ]);
        triggerSuccessToast(t('Stripe configuration updated successfully'));
        handleSettingsClose();
      },
      onError: (error) => {
        triggerToast(t('Failed to update Stripe configuration'));
      },
    },
  });

  const { mutate: connectStripe, isLoading: isConnectingStripe } =
    useConnectStripe({
      options: {
        onSuccess: (data) => {
          if (data.onboardingLink) {
            window.open(data.onboardingLink, '_blank');
          }
          triggerSuccessToast(t('Stripe connected successfully'));
          queryClient.invalidateQueries([
            QUERY_KEYS.GET_ENVELOPE_DETAILS,
            envelopeId,
          ]);
        },
        onError: () => {
          triggerToast(t('Failed to connect Stripe'));
        },
      },
    });

  const { mutate: disconnectStripe, isLoading: isDisconnecting } =
    useDisconnectStripe({
      options: {
        onSuccess: (data) => {
          queryClient.invalidateQueries([
            QUERY_KEYS.GET_ENVELOPE_DETAILS,
            envelopeId,
          ]);
        },
        onError: () => {
          triggerToast(t('Failed to disconnect Stripe'));
        },
      },
    });

  const targetColumnField = watch('statusColumnId');
  const isPaymentReminderEnabled = watch('isPaymentReminderEnabled');

  const statusOptions = useMemo(() => {
    const options = queryData?.data?.items[0]?.column_values
      ?.filter((column) => ['status'].includes(column.type))
      ?.map((x) => {
        return { value: x.id, label: x.title };
      });

    if (Array.isArray(options)) {
      options.push({
        value: 'add-new-column',
        label: `+ ${t('Add new column')}`,
        onClick: () => {
          setShowAddColumnDialog({ show: true, type: 'status' });
        },
      });
    }

    return options;
  }, [queryData]);

  const transactionIdOptions = useMemo(() => {
    const options = queryData?.data?.items[0]?.column_values
      ?.filter((column) => ['text'].includes(column.type))
      ?.map((x) => {
        return { value: x.id, label: x.title };
      });

    if (Array.isArray(options)) {
      options.push({
        value: 'add-new-column',
        label: `+ ${t('Add new column')}`,
        onClick: () => {
          setShowAddColumnDialog({ show: true, type: 'text' });
        },
      });
    }

    return options;
  }, [queryData]);

  const targetColumnOptions = useMemo(() => {
    if (!targetColumnField) return [];

    return (
      Object.entries(
        JSON.parse(
          queryData?.data?.items[0]?.column_values?.find(
            (column) => column.id === targetColumnField,
          )?.column?.settings_str || '{}',
        )?.labels || {},
      )?.map(([labelIdx, label]) => ({
        value: labelIdx,
        label: label,
      })) || []
    );
  }, [queryData, targetColumnField]);

  const onSubmit = (data) => {
    const stripeConfiguration = {
      ...data,
    };

    updateStripeConfig(stripeConfiguration);
  };

  const isStripeConnected = envelopeDetails?.isStripeConnected;

  return (
    <SettingsDialogContainer onSubmit={handleSubmit(onSubmit)}>
      <div className="title">{t('Collect Payments vis Stripe')}</div>
      {isLoadingStripeConnectionStatus ? (
        <>Loading...</>
      ) : (
        <>
          <hr />
          <div className="content">
            {isConnectingStripe ? (
              <>Connecting with stripe</>
            ) : isDisconnecting ? (
              <>Disconnecting with stripe</>
            ) : (
              <StripeButton
                className="h-[32px] w-[143px] cursor-pointer"
                src={
                  envelopeDetails?.isStripeConnected
                    ? DisconnectStripe
                    : ConnectStripe
                }
                alt={
                  envelopeDetails?.isStripeConnected
                    ? 'Disconnect Stripe'
                    : 'Connect Stripe'
                }
                onClick={() =>
                  envelopeDetails?.isStripeConnected
                    ? disconnectStripe()
                    : connectStripe()
                }
              />
            )}
            {isStripeConnected ? (
              <>
                <PaymentConfigurationFields>
                  <div>
                    <section class="label-component--wrapper">
                      <label for="input" className="">
                        Payment Status <span className="text-red-600">*</span>
                      </label>
                    </section>
                    <Controller
                      name="statusColumnId"
                      control={control}
                      render={({ field }) => (
                        <>
                          <StyledDropdown
                            clearable={false}
                            options={statusOptions || []}
                            value={statusOptions?.find(
                              (i) => i.value === field.value,
                            )}
                            placeholder={`${t('Payment Status')}`}
                            className="dropdown-stories-styles_with-chips"
                            onChange={(e) => {
                              if (e?.onClick) {
                                e.onClick();
                              } else field.onChange(e?.value);
                            }}
                          />
                          {errors?.statusColumnId?.message ? (
                            <span className="text-sm text-red-500">
                              {errors?.statusColumnId?.message}
                            </span>
                          ) : null}
                        </>
                      )}
                    />
                  </div>
                  <div>
                    <section class="label-component--wrapper">
                      <label for="input" className="">
                        Payment Status Label
                        <span className="text-red-600">*</span>
                      </label>
                    </section>
                    <Controller
                      name="statusColumnLabel"
                      control={control}
                      render={({ field }) => (
                        <>
                          <StyledDropdown
                            clearable={false}
                            options={targetColumnOptions || []}
                            value={targetColumnOptions?.find(
                              (i) => i.value === field.value,
                            )}
                            placeholder={`${t('Payment Status Label')}`}
                            className="dropdown-stories-styles_with-chips"
                            onChange={(e) => field.onChange(e?.value)}
                          />

                          {errors?.statusColumnLabel?.message ? (
                            <span className="text-sm text-red-500">
                              {errors?.statusColumnLabel?.message}
                            </span>
                          ) : null}
                        </>
                      )}
                    />
                  </div>
                  <div>
                    <section class="label-component--wrapper">
                      <label for="input" className="">
                        Payment Transaction Id{' '}
                        <span className="text-red-600">*</span>
                      </label>
                    </section>
                    <Controller
                      name="transactionIdColumnId"
                      control={control}
                      render={({ field }) => (
                        <>
                          <StyledDropdown
                            clearable={false}
                            options={transactionIdOptions || []}
                            value={transactionIdOptions?.find(
                              (i) => i.value === field.value,
                            )}
                            placeholder={`${t('Payment Transaction Id')}`}
                            className="dropdown-stories-styles_with-chips"
                            onChange={(e) => {
                              if (e?.onClick) {
                                e.onClick();
                              } else field.onChange(e?.value);
                            }}
                          />
                          {errors?.transactionIdColumnId?.message ? (
                            <span className="text-sm text-red-500">
                              {errors?.transactionIdColumnId?.message}
                            </span>
                          ) : null}
                        </>
                      )}
                    />
                  </div>
                </PaymentConfigurationFields>
                <hr />
                <PaymentReminderContainer>
                  <span className="inline">
                    Send payment email after document is signed and a remind
                    after
                    <Controller
                      name="isPaymentReminderEnabled"
                      control={control}
                      render={({ field }) => (
                        <>
                          <Toggle
                            isSelected={field.value}
                            onChange={(e) => field.onChange(e)}
                          />
                        </>
                      )}
                    />
                  </span>
                  {isPaymentReminderEnabled ? (
                    <Controller
                      name="remindAfter"
                      control={control}
                      render={({ field }) => (
                        <>
                          <StyledDropdown
                            clearable={false}
                            options={DAY_OPTIONS || []}
                            value={DAY_OPTIONS?.find(
                              (i) => i.value === field.value,
                            )}
                            placeholder={`${t('Days')}`}
                            className="dropdown-stories-styles_with-chips"
                            onChange={(e) => field.onChange(e?.value)}
                          />
                          {errors?.remindAfter?.message ? (
                            <span className="text-sm text-red-500">
                              {errors?.remindAfter?.message}
                            </span>
                          ) : null}
                        </>
                      )}
                    />
                  ) : null}
                </PaymentReminderContainer>
              </>
            ) : null}
          </div>
        </>
      )}
      <hr />
      <SaveSettingsContainer>
        <CustomButton
          kind={Button.kinds.PRIMARY}
          type="submit"
          loading={isLoading}
          disabled={isLoading}
        >
          {t('Save')}
        </CustomButton>
      </SaveSettingsContainer>
    </SettingsDialogContainer>
  );
};

export default PaymentControllerDialog;
